// Web Navigation Hooks - v2.0 (Platform check FIRST)
// This module provides React Navigation compatible hooks for web
import { useNavigation as useReactNavigation, useRoute as useReactRoute } from '@react-navigation/native'
import { Platform } from 'react-native'
import { getData } from '../components/appBar/context/HeaderContext'

// Browser history (popstate) sync lives in HeaderContext's DataState provider,
// which owns the navigation stack and is always mounted on web.

/**
 * useRoute hook compatible with web
 * On web, reads from HeaderContext
 * On native, uses React Navigation's useRoute
 * 
 * IMPORTANT: Platform check MUST happen before calling React Navigation hooks
 * This prevents the "Couldn't find a route object" error on web
 */
export const useRoute = () => {
    // console.log('[useRoute] Platform.OS =', Platform.OS)
    
    // Check platform FIRST to avoid calling React Navigation hooks on web
    if (Platform.OS === 'web') {
        const { navigationParams, footerRoute } = getData()
        // console.log('[useRoute][web] footerRoute:', footerRoute, 'params:', navigationParams)
        
        return {
            params: navigationParams || {},
            name: footerRoute,
            key: footerRoute,
        }
    }
    
    // Only call React Navigation hook on native platforms
    const reactRoute = useReactRoute()
    return reactRoute
}

/**
 * useNavigation hook compatible with web
 * On web, uses HeaderContext to manage navigation
 * On native, uses React Navigation's useNavigation
 * 
 * IMPORTANT: Platform check MUST happen before calling React Navigation hooks
 * This prevents the "Couldn't find a route object" error on web
 */
export const useNavigation = () => {
    // Check platform FIRST to avoid calling React Navigation hooks on web
    if (Platform.OS === 'web') {
    const { setCurrentFooterRoute, footerRoute } = getData()

        return {
            navigate: (routeName, params) => {
                // console.log('[useNavigation][web] navigate to:', routeName, 'params:', params)
                setCurrentFooterRoute(routeName, params)
            },
            reset: ({ index, routes }) => {
                try {
                    const last = routes && routes.length ? routes[routes.length - 1] : { name: 'LearningHome', params: {} }
                    console.log('[useNavigation][web] reset to:', last)
                    setCurrentFooterRoute(last.name, last.params)
                } catch (e) {
                    console.warn('[useNavigation][web] reset error:', e)
                }
            },
            goBack: () => {
                // Pedimos un back real al navegador en vez de mutar el stack
                // a mano: así un botón "volver" en pantalla y el botón atrás
                // físico del navegador terminan en el mismo lugar (el listener
                // de 'popstate' en HeaderContext), sin duplicar la lógica ni
                // desincronizar el historial real con el routeStack interno.
                try {
                    if (footerRoute === 'PostsList' || typeof window === 'undefined') {
                        // No hay nada atrás dentro de la app: no tocamos el
                        // historial del navegador para no sacar al usuario.
                        return
                    }
                    window.history.back()
                } catch (e) {
                    // fallback
                    setCurrentFooterRoute('LearningHome', {})
                }
            },
            canGoBack: () => footerRoute !== 'PostsList',
            getState: () => ({
                routes: [{ name: footerRoute }],
                index: 0,
            }),
            isFocused: () => true,
            addListener: (eventName, callback) => {
                // console.log('[useNavigation][web] addListener:', eventName)
                // For web, we DON'T call the callback immediately to avoid infinite loops
                // The component should handle initial data fetch in its own useEffect
                // Return unsubscribe function
                return () => {
                    // console.log('[useNavigation][web] unsubscribe from:', eventName)
                }
            },
            removeListener: (eventName, callback) => {
                console.log('[useNavigation][web] removeListener:', eventName)
            },
        }
    }
    
    // Only call React Navigation hook on native platforms
    const reactNavigation = useReactNavigation()
    return reactNavigation
}
